import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchBlogs } from "../../../api/blogsAPI";
import Slider from "react-slick";
import { Link } from "react-router-dom";
import { placeholderImage, isValidUrl } from "../../../components/invalidImage";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import DOMPurify from "dompurify";

function NextArrow(props) {
  const { onClick } = props;
  return (
    <button
      className="absolute top-1/2 -right-2 sm:-right-4 transform -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-medium hover:shadow-strong hover:-translate-y-[calc(50%+2px)] text-slate-700 hover:text-brand-600 cursor-pointer z-10 transition-all flex items-center justify-center"
      onClick={onClick}
      aria-label="Next"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

function PrevArrow(props) {
  const { onClick } = props;
  return (
    <button
      className="absolute top-1/2 -left-2 sm:-left-4 transform -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-medium hover:shadow-strong hover:-translate-y-[calc(50%+2px)] text-slate-700 hover:text-brand-600 cursor-pointer z-10 transition-all flex items-center justify-center"
      onClick={onClick}
      aria-label="Previous"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );
}

const stripHtml = (h) => h.replace(/<[^>]*>/g, "");
const previewText = (html, n = 150) => {
  const text = stripHtml(DOMPurify.sanitize(html));
  return text.length > n ? `${text.slice(0, n)}...` : text;
};

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getBlogs = async () => {
      try {
        const blogsData = await fetchBlogs();
        setBlogs(blogsData);
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };
    getBlogs();
  }, []);

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const chunkedBlogs = [];
  for (let i = 0; i < blogs.length; i += 2) {
    chunkedBlogs.push(blogs.slice(i, i + 2));
  }

  return (
    <section
      id="Blog"
      className="relative py-20 md:py-28 bg-slate-50 overflow-hidden"
    >
      <div className="absolute top-10 right-10 w-72 h-72 bg-brand-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-72 h-72 bg-eco-100/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10" id="BlogsPage">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10"
        >
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-brand-50 text-brand-700 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Insight & Artikel
            </span>
            <h2 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
              Pilihan{" "}
              <span className="text-gradient">Blog Terbaik</span>
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 font-semibold font-overpass group no-underline hover:no-underline self-start md:self-end"
          >
            Jelajahi Defra Blog
            <span className="inline-block transition-transform group-hover:translate-x-1">
              →
            </span>
          </Link>
        </motion.div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="h-64 rounded-2xl bg-slate-100 animate-pulse-soft"
              />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-red-500">Error: {error}</p>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Slider {...settings} className="mx-2 lg:mx-6">
              {chunkedBlogs.map((chunk, index) => (
                <div key={index} className="px-2 py-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {chunk.map((blog) => (
                      <Link
                        to={`/blog/${blog.id}`}
                        key={blog.id}
                        className="group flex flex-col sm:flex-row gap-4 p-4 bg-white rounded-3xl border border-slate-100 shadow-soft hover:shadow-medium card-hover no-underline hover:no-underline"
                      >
                        <div className="relative w-full sm:w-44 h-48 sm:h-44 flex-shrink-0 rounded-2xl overflow-hidden">
                          <img
                            src={
                              isValidUrl(blog.image)
                                ? blog.image
                                : placeholderImage
                            }
                            alt={blog.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="flex flex-col justify-between flex-1 min-w-0">
                          <div>
                            <h3 className="font-oswald text-lg sm:text-xl font-semibold text-slate-900 group-hover:text-brand-600 transition-colors line-clamp-2 mb-2">
                              {blog.title}
                            </h3>
                            <p className="text-sm text-slate-600 font-overpass line-clamp-3 leading-relaxed">
                              {previewText(blog.description, 140)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 font-overpass">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white text-xs font-bold">
                              {blog.author?.charAt(0).toUpperCase() || "D"}
                            </div>
                            <span className="font-medium text-slate-700">
                              {blog.author}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span>
                              {new Date(blog.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </Slider>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default BlogsPage;
